export const MindscriptService = {
  evaluateExpression(expression, playbackStore) {
    log.info(3,"🔎 MindscriptService evaluateExpression", expression)
    log.info(3,"🔎 MindscriptService Current Variables", JSON.stringify(playbackStore.variables))
    // Remove {{ and }} from expression and trim whitespace from ends only
    const cleanExpr = expression.replace(/{{|}}/g, '').trim()
    
    // Split by operators while preserving operators and handling quoted strings
    const tokens = this.tokenize(cleanExpr)

    // Preserve non-arithmetic function return values like argmax_name(...)
    if (tokens.length === 1 && this.isSupportedFunctionCall(tokens[0])) {
      return this.resolveToken(tokens[0], playbackStore)
    }
    
    // Check if any token resolves to a non-numeric string (excluding undefined variables)
    const hasStringToken = tokens.some((token, index) => {
      log.info(3,"🔎 MindscriptService hasStringToken", token)
      // Skip operators
      if (token === '+' || token === '-') return false
      const value = this.resolveToken(token, playbackStore)
      const isUndefinedVariable = this.isUndefinedVariable(token, playbackStore)
      // If it's an undefined variable, don't count it as a string token for arithmetic
      if (isUndefinedVariable) return false
      return !this.isNumeric(value)
    })

    // If we have any strings, convert everything to strings and only allow concatenation
    if (hasStringToken) {
      // Start with first token
      let result = this.resolveToken(tokens[0], playbackStore)
      result = String(result)
      
      for (let i = 1; i < tokens.length; i += 2) {
        const operator = tokens[i]
        const nextToken = tokens[i + 1]
        
        if (!nextToken) continue
        
        const nextValue = this.resolveToken(nextToken, playbackStore)
        
        if (operator === '+') {
          result = result + String(nextValue)
        }
        // Ignore subtraction in string mode
      }
      return result
    } else {
      log.info(3,"🔎 MindscriptService numeric mode")
      // All tokens are numeric, do math operations
      // Start with first token, convert undefined variables to 0
      let result
      if (this.isUndefinedVariable(tokens[0], playbackStore)) {
        result = 0
        log.info(3,"🔎 First token is undefined variable, setting to 0")
      } else {
        const firstValue = this.resolveToken(tokens[0], playbackStore)
        result = Number(firstValue)
        log.info(3,"🔎 First token resolved to:", firstValue, "converted to number:", result)
      }
      
      for (let i = 1; i < tokens.length; i += 2) {
        const operator = tokens[i]
        const nextToken = tokens[i + 1]
        
        if (!nextToken) continue
        
        let nextValue
        if (this.isUndefinedVariable(nextToken, playbackStore)) {
          nextValue = 0
          log.info(3,"🔎 Token", nextToken, "is undefined variable, using 0")
        } else {
          const resolvedValue = this.resolveToken(nextToken, playbackStore)
          nextValue = Number(resolvedValue)
          log.info(3,"🔎 Token", nextToken, "resolved to:", resolvedValue, "converted to number:", nextValue)
        }
        
        if (operator === '+') {
          log.info(3,"🔎 Adding:", result, "+", nextValue)
          result += nextValue
        } else if (operator === '-') {
          log.info(3,"🔎 Subtracting:", result, "-", nextValue)
          result -= nextValue
        }
        log.info(3,"🔎 Result after operation:", result)
      }
      return result
    }
    
    return result
  },
  
  tokenize(expr) {
    const tokens = []
    let current = ''
    let quoteChar = null
    let parenDepth = 0
    
    for (let i = 0; i < expr.length; i++) {
      const char = expr[i]
      
      if ((char === '"' || char === "'") && expr[i - 1] !== '\\') {
        if (quoteChar === char) {
          quoteChar = null
        } else if (!quoteChar) {
          quoteChar = char
        }
        current += char
      } else if (!quoteChar && char === '(') {
        parenDepth += 1
        current += char
      } else if (!quoteChar && char === ')') {
        parenDepth = Math.max(parenDepth - 1, 0)
        current += char
      } else if ((char === '+' || char === '-') && !quoteChar && parenDepth === 0) {
        if (current.trim()) {
          tokens.push(current.trim())
        }
        tokens.push(char)
        current = ''
      } else {
        current += char
      }
    }
    
    if (current.trim()) {
      tokens.push(current.trim())
    }
    
    return tokens
  },
  
  resolveToken(token, playbackStore) {
    token = token.trim()

    const functionCall = this.parseFunctionCall(token)
    if (functionCall) {
      const result = this.evaluateFunction(functionCall.name, functionCall.args, playbackStore)
      if (result !== undefined) {
        log.info(3,"🔎 MindscriptService resolveToken function", functionCall.name, functionCall.args, result)
        return result
      }
    }
    
    // Check if token is a quoted string
    if (token.startsWith('"') && token.endsWith('"')) {
      return token.slice(1, -1)  // Remove quotes but preserve internal spaces
    } else if (token.startsWith("'") && token.endsWith("'")) {
      return token.slice(1, -1)  // Remove quotes but preserve internal spaces
    }
    
    // Check if token is a number literal
    if (this.isNumeric(token)) {
      log.info(3,"🔎 MindscriptService resolveToken", token, token, "(numeric literal)")
      return token
    }
    
    // If it's a variable name, get its value
    const value = playbackStore.getVariable(token)
    log.info(3,"🔎 MindscriptService resolveToken", token, value)
    if (value !== undefined) {
      return value
    }
    
    // If variable doesn't exist, return the token name itself
    return token
  },

  // New helper method to check if a token is an undefined variable
  isUndefinedVariable(token, playbackStore) {
    token = token.trim()

    if (this.isSupportedFunctionCall(token)) {
      return false
    }
    
    // Skip quoted strings - they're not variables
    if ((token.startsWith('"') && token.endsWith('"')) || 
        (token.startsWith("'") && token.endsWith("'"))) {
      return false
    }
    
    // Skip numeric literals - they're not variables
    if (this.isNumeric(token)) {
      return false
    }
    
    // Check if the variable is undefined
    const value = playbackStore.getVariable(token)
    return value === undefined
  },

  isNumeric(value) {
    // Handle empty values
    if (!value && value !== 0) return false
    
    // Handle already-numeric values
    if (typeof value === 'number') return true
    
    // Handle string values that can be converted to numbers
    if (typeof value === 'string') {
      // Remove whitespace
      const trimmed = value.trim()
      // Check if it can be converted to a number and is not empty
      return trimmed !== '' && !isNaN(trimmed)
    }
    
    return false
  },

  isFunctionCall(token) {
    return !!this.parseFunctionCall(token)
  },

  isSupportedFunctionCall(token) {
    const functionCall = this.parseFunctionCall(token)
    return !!functionCall && this.isSupportedFunctionName(functionCall.name)
  },

  isSupportedFunctionName(name) {
    return ['argmax_name'].includes((name || '').toLowerCase())
  },

  parseFunctionCall(token) {
    const trimmed = (token || '').trim()
    if (!trimmed) return null

    const match = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\((.*)\)$/)
    if (!match) return null

    return {
      name: match[1],
      args: this.parseArguments(match[2])
    }
  },

  parseArguments(argString) {
    const args = []
    let current = ''
    let quoteChar = null
    let parenDepth = 0

    for (let i = 0; i < argString.length; i++) {
      const char = argString[i]

      if ((char === '"' || char === "'") && argString[i - 1] !== '\\') {
        if (quoteChar === char) {
          quoteChar = null
        } else if (!quoteChar) {
          quoteChar = char
        }
        current += char
        continue
      }

      if (!quoteChar && char === '(') {
        parenDepth += 1
        current += char
        continue
      }

      if (!quoteChar && char === ')') {
        parenDepth = Math.max(parenDepth - 1, 0)
        current += char
        continue
      }

      if (!quoteChar && parenDepth === 0 && char === ',') {
        if (current.trim()) {
          args.push(current.trim())
        }
        current = ''
        continue
      }

      current += char
    }

    if (current.trim()) {
      args.push(current.trim())
    }

    return args
  },

  evaluateFunction(name, args, playbackStore) {
    if (!this.isSupportedFunctionName(name)) {
      return undefined
    }

    const functionName = (name || '').toLowerCase()

    switch (functionName) {
      case 'argmax_name':
        return this.evaluateArgmaxName(args, playbackStore)
    }
  },

  evaluateArgmaxName(args, playbackStore) {
    let winnerName = null
    let winnerValue = null

    args.forEach((arg) => {
      const variableName = this.normalizeFunctionArgument(arg)
      if (!variableName) return

      const rawValue = playbackStore.getVariable(variableName)
      if (!this.isNumeric(rawValue)) return

      const numericValue = Number(rawValue)
      if (winnerName === null || numericValue > winnerValue) {
        winnerName = variableName
        winnerValue = numericValue
      }
    })

    return winnerName
  },

  normalizeFunctionArgument(arg) {
    const value = (arg || '').trim()
    if (!value) return null

    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      return value.slice(1, -1).trim()
    }

    return value
  }
}
